import { useState, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
    CreateCourseDocument,
    CourseManagerTokenDocument,
    IFeature,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useFrontendPermissions } from 'user/hooks';
import { LoaderOverlay } from 'common/components/Loader';
import { CourseCreateModal } from 'training/components/CourseCreateModal';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { getUrl } from 'route/utils/getUrl';
import { CourseAICreateModal } from 'training/components/CourseAICreateModal';
import { useFeature } from 'common/hooks/useFeature';

interface IChildrenParams {
    openCreateModal(): void;
    openAICreateModal(): void;
    createCourse(templateId?: string): void;
}

interface IProps {
    children(params: IChildrenParams): React.ReactNode;
    onCourseReturnUrl?(courseId: string): string; // Url to redirect to after course editing in elearning tool
}

export const CourseCreateAction = ({ children, onCourseReturnUrl }: IProps) => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const isCourseCreate = !!useRouteMatch('COURSE_CREATE');

    const [showModal, setShowModal] = useState(isCourseCreate);
    const [showAIModal, setShowAIModal] = useState(false);
    const { showApolloError } = useApolloError();

    const [createCourse, { loading: loadingCreate }] =
        useMutation(CreateCourseDocument);

    const { canCreate: canCreateCourse, loading: loadingPermissions } =
        useFrontendPermissions('course');

    const { canUse: canUseAI } = useFeature(IFeature.AI);

    // When component is rendered and the route is changed to COURSE_CREATE, show the modal.
    useEffect(() => {
        if (isCourseCreate) setShowModal(true);
    }, [isCourseCreate]);

    const [
        getCourseManagerToken,
        { called: managerTokenCalled, error: managerTokenError },
    ] = useLazyQuery(CourseManagerTokenDocument);

    const handleCreateCourse = async (templateId?: string) => {
        let url;

        try {
            const courseResponse = await createCourse({
                variables: { course: { templateId } },
            });
            const courseId = courseResponse.data?.createCourse?.course.id;

            if (!courseId) return;

            const returnUrl = onCourseReturnUrl?.(courseId);
            const tokenResponse = await getCourseManagerToken({
                variables: { id: courseId, returnUrl },
            });

            url = tokenResponse.data?.courseManagerToken?.url;
        } catch (error) {
            showApolloError(error);

            return;
        }

        if (!url) return;

        // Redirect to edit course
        window.location.href = url;
    };

    if (!loadingPermissions && !canCreateCourse) return null;

    return (
        <>
            {children({
                openCreateModal: () => setShowModal(true),
                openAICreateModal: () => setShowAIModal(true),
                createCourse: handleCreateCourse,
            })}

            <CourseCreateModal
                open={showModal}
                onClose={() => {
                    setShowModal(false);

                    if (isCourseCreate) {
                        navigate(getUrl('COURSE_MANAGEMENT_LIST'));
                    }
                }}
                onCreateAICourse={() => setShowAIModal(true)}
                onCreateCourse={handleCreateCourse}
            />

            {canUseAI && (
                <CourseAICreateModal
                    open={showAIModal}
                    onClose={() => setShowAIModal(false)}
                />
            )}

            <LoaderOverlay
                fadeIn
                visible={
                    loadingCreate ||
                    // Use "called" + "error" check here to prevent it from hidding while it's
                    // still busy with the redirect
                    (managerTokenCalled && !managerTokenError)
                }
            >
                {translate('courseCreateModal.creatingCourse')}
            </LoaderOverlay>
        </>
    );
};
